import { t } from "../contract/strings";
import { useApp } from "../state/store";
import { Button } from "./button";
import { cx } from "./cx";
import { ProgressBar } from "./progress-bar";

export interface ModelProgressProps {
  /** Try again after a failed load. */
  onRetry: () => void;
  /** Stop waiting. The load itself keeps going in the background. */
  onCancel?: () => void;
  className?: string;
}

/**
 * "Getting ready…" while a screen waits for the voice model, and the plain error with Try
 * again when it could not load (#658, #659).
 *
 * The copy that used to be boot's overlay, moved to the screens that need the model. It reads
 * the shared readiness from the store, so every waiter shows the one load's real progress.
 * The one-time size note shows only when this load has to download, which on a metered
 * connection is the first moment the person learns it will.
 */
export function ModelProgress({ onRetry, onCancel, className }: ModelProgressProps) {
  const model = useApp((s) => s.model);
  if (model.status === "failed") {
    return (
      <div className={cx("grid gap-2 w-full", className)}>
        <p role="alert" className="m-0 text-note text-danger">
          {model.failure === "offline" ? t("model.offline") : t("model.failed")}
        </p>
        <Button variant="ghost" onClick={onRetry}>
          {t("common.try_again")}
        </Button>
        {onCancel ? (
          <Button variant="link" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        ) : null}
      </div>
    );
  }
  const percent = model.fraction === null ? null : Math.round(model.fraction * 100);
  return (
    <div className={cx("grid gap-2 w-full", className)} aria-live="polite">
      <ProgressBar fraction={model.fraction ?? 0} indeterminate={model.fraction === null} className="w-full" />
      <p className="m-0 text-note text-ink">
        {percent === null ? t("model.getting_ready") : t("model.getting_ready_percent", { percent })}
      </p>
      {model.download ? <p className="m-0 text-note text-ink-soft">{t("model.size_note")}</p> : null}
      {onCancel ? (
        <Button variant="ghost" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
      ) : null}
    </div>
  );
}
